import http from "./http-common";
import Cookies from "js-cookie";

class RestApi {
  // Helper methods for getting user data
  getUserId() {
    return Cookies.get('auth_user_id') || localStorage.getItem('_id');
  }

  getToken() {
    return Cookies.get('auth_token') || localStorage.getItem('token');
  }

  // User APIs
  validateAddress(data) {
    return http.post("/validateAddress", data);
  }

  signin(data) {
    return http.post("/signin", data);
  }

  googleSignin(data) {
    return http.post("/googleSignin", data);
  }

  changePassword(data) {
    return http.post("/changePassword", data);
  }

  signUp(data) {
    return http.post("/signup", data);
  }

  googleSignUp(data) {
    return http.post("/googleSignup", data);
  }

  verifyEmail(token) {
    return http.get(`/verify-email?token=${token}`);
  }

  resetPasswordRequest(data) {
    return http.post("/resetPasswordRequest", data);
  }

  resetPassword(data) {
    return http.post(`/reset-password`, data);
  }

  resetPasswordToken(token) {
    return http.get(`/reset-password?token=${token}`);
  }

  updateUser(data) {
    // Ensure user_id is included if not provided
    const userId = data.id || data.user_id || this.getUserId();
    const updatedData = { ...data, id: userId };
    return http.post('/updateUser', updatedData);
  }

  getUserSubscriptions(userId) {
    return http.get(`/subscription/settings/${userId}`);
  }

  // QR code APIs
  createQR(data) {
    // Ensure user_id is included
    const userId = data.user_id || this.getUserId();
    const updatedData = { ...data, user_id: userId };
    return http.post("/createQr", updatedData);
  }

  getUrlLink(data) {
    return http.get(`/redirect/${data.qr_short_id}`);
  }

  updateQr(data) {
    return http.put("/updateQr", data);
  }

  getQrById(qrId) {
    return http.get(`/fetchQrById/${qrId}`);
  }

  getQrByUserid(data) {
    // If no user_id provided, use the one from auth
    const userId = data.user_id || this.getUserId();
    const updatedData = { ...data, user_id: userId };
    return http.post("/fetchQrByUserid", updatedData);
  }

  deactivateQr(id) {
    return http.post(`/deleteQr/${id}`);
  }

  // Premium subscription APIs
  initiateSubscription(data) {
    // Transform the data to match backend expectations
    const subscriptionData = {
      user_id: data.user_id || this.getUserId(),
      user_email: data.user_email || Cookies.get('auth_email') || localStorage.getItem('email'),
      plan_selected: data.plan_selected,
      duration_months: this.extractDuration(data.plan_selected),
      amount: typeof data.amount === 'string' ? parseFloat(data.amount) : data.amount,
      currency: data.currency || 'INR'
    };
    
    return http.post("/subscription/initiate", subscriptionData);
  }

  verifyAndActivateSubscription(data) {
    const verifyData = {
      order_id: data.order_id,
      payment_id: data.payment_id,
      signature: data.signature,
      user_id: data.user_id || this.getUserId()
    };
    
    return http.post("/subscription/verify", verifyData);
  }

  // Helper methods for plan conversion
  convertPlanType(planSelected) {
    if (planSelected === "Enterprise") return "ENTERPRISE";
    if (planSelected === "Free") return "FREE";
    if (planSelected === "Growth") return "GROWTH";
    if (planSelected === "Premium") return "PREMIUM";
    return planSelected; // Return as-is if not matching any known type
  }

  extractDuration(planSelected) {
    if (planSelected === "Free") return 0;
    if (planSelected === "Growth") return 12; // Annual plan
    if (planSelected === "Premium") return 12; // Annual plan
    if (planSelected === "Enterprise") return 12; // Annual plan
    
    // Try to parse as number for legacy support
    const months = parseInt(planSelected);
    return isNaN(months) ? 1 : months;
  }

  // Legacy method names for backward compatibility
  buyPremium(data) {
    return this.initiateSubscription(data);
  }

  verifyPaymentStatus(data) {
    return this.verifyAndActivateSubscription(data);
  }

  updatePaymentStatus(data) {
    const updatedData = {
      ...data,
      user_id: data.user_id || this.getUserId()
    };
    return http.post("/updatePayment", updatedData);
  }

  cancelSubscription(data) {
    const cancelData = {
      ...data,
      user_id: data.user_id || this.getUserId()
    };
    return http.post("/subscription/cancel", cancelData);
  }

  getSubscriptionDetails(userId) {
    const id = userId || this.getUserId();
    return http.get(`/subscription/${id}`);
  }

  checkSubscriptionStatus(userId) {
    const id = userId || this.getUserId();
    return http.get(`/subscription/status/check?user_id=${id}`);
  }

  // Smart Link APIs
  createSmartLink(data) {
    const userId = data.user_id || this.getUserId();
    const updatedData = { ...data, user_id: userId };
    return http.post('/createSmartLink', updatedData);
  }

  redirectSmartLink(id) {
    return http.get(`/r/${id}`);
  }

  getSmartLinkbyId(id) {
    return http.get(`/fetchSmartLinkById/${id}`);
  }

  getSmartLinkbyUser(id) {
    const userId = id || this.getUserId();
    return http.post(`/fetchSmartLinkByUser/${userId}`);
  }

  updateSmartLink(data) {
    return http.post(`/updateSmartLink/${data.id}`, data);
  }

  deleteSmartLink(id) {
    return http.delete(`/deleteSmartLink/${id}`);
  }

  // Bulk operations
  createMultipleQRs(data) {
    const userId = data.user_id || this.getUserId();
    const updatedData = { ...data, user_id: userId };
    return http.post("/bulk/create", updatedData);
  }

  getQRJobs() {
    return http.get("/qr-jobs");
  }

  getQrJobsById(id) {
    return http.get(`/qr-jobs/${id}`);
  }

  createMultipleSmartlinks(data) {
    const userId = data.user_id || this.getUserId();
    const updatedData = { ...data, user_id: userId };
    return http.post("/bulkSmartLink/create", updatedData);
  }

  getUserSmartLinkCount(userId) {
    const id = userId || this.getUserId();
    return http.get(`/smartlinks/user/${id}/count`);
  }
  updateBillingCycle(data) {
    const updateData = {
      user_id: data.user_id || this.getUserId(),
      billing_cycle: data.billing_cycle
    };
    return http.post("/subscription/update-billing", updateData);
  }
  getUserQRCount(data) {
    // If no user_id provided, use the one from auth
    const userId = data && data.user_id ? data.user_id : this.getUserId();
  return http.get(`/qr-limit-check/${userId}`);
  }
}

export default new RestApi();